'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useLang } from './LanguageContext';

const destinations = [
{
  key: 'dubai',
  img: "https://images.unsplash.com/photo-1617559057121-5ccad3b7571b",
  alt: 'Dubai skyline with Burj Khalifa at night reflecting in water',
  size: 'large', // col-span-2 row-span-2
  flag: '🇦🇪'
},
{
  key: 'samarkand',
  img: "https://images.unsplash.com/photo-1726910921322-9032a3f3240b",
  alt: 'Registan Square in Samarkand with blue tiled domes and minarets',
  size: 'medium',
  flag: '🇺🇿'
},
{
  key: 'bukhara',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_17ff193f8-1772304046750.png",
  alt: 'Kalon Minaret in Bukhara Uzbekistan ancient brick tower at sunset',
  size: 'medium',
  flag: '🇺🇿'
},
{
  key: 'istanbul',
  img: "https://images.unsplash.com/photo-1504215485637-6154fc7e5bf1",
  alt: 'Istanbul Blue Mosque and Hagia Sophia at sunset over the Bosphorus',
  size: 'medium',
  flag: '🇹🇷'
},
{
  key: 'maldives',
  img: "https://images.unsplash.com/photo-1683650904081-78afc08950cb",
  alt: 'Maldives overwater bungalows on crystal clear turquoise lagoon',
  size: 'medium',
  flag: '🇲🇻'
},
{
  key: 'paris',
  img: "https://images.unsplash.com/photo-1647201807036-47eca70336f2",
  alt: 'Eiffel Tower Paris at twilight with city lights twinkling',
  size: 'medium',
  flag: '🇫🇷'
}];


export default function DestinationsSection() {
  const { t } = useLang();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const reveals = sectionRef?.current?.querySelectorAll('.reveal');
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="destinations" ref={sectionRef} className="py-24 md:py-32">
      <div className="max-w-8xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="section-label mb-4 reveal">{t('dest_label')}</div>
            <h2
              className="font-serif font-bold text-cream reveal reveal-delay-1"
              style={{
                fontFamily: 'Fraunces, serif',
                fontSize: 'clamp(2.2rem, 5vw, 4rem)',
                lineHeight: 1.1
              }}>
              
              {t('dest_title')}
            </h2>
          </div>
          <p className="text-muted text-base leading-relaxed max-w-sm reveal reveal-delay-2">
            {t('dest_subtitle')}
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[280px]">
          {/* Large card — Dubai */}
          <div className="dest-card md:col-span-2 md:row-span-2 reveal">
            <AppImage
              src={destinations?.[0]?.img}
              alt={destinations?.[0]?.alt}
              fill
              className="object-cover" />
            
            <div className="dest-card-overlay" />
            <div className="absolute inset-0 p-8 flex flex-col justify-end">
              <span className="text-2xl mb-2">{destinations?.[0]?.flag}</span>
              <h3
                className="font-serif font-bold text-cream text-3xl md:text-4xl mb-2"
                style={{ fontFamily: 'Fraunces, serif' }}>
                
                {t('dest_dubai')}
              </h3>
              <p className="text-cream/60 text-sm mb-4">{t('dest_dubai_desc')}</p>
              <a href="#contact" className="inline-flex items-center gap-2 text-gold text-xs font-bold tracking-widest uppercase group">
                {t('dest_explore')}
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                  className="transition-transform group-hover:translate-x-1">
                  
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>

          {/* Small cards */}
          {destinations?.slice(1)?.map((dest, i) =>
          <div
            key={dest?.key}
            className={`dest-card reveal`}
            style={{ transitionDelay: `${(i + 1) * 0.08}s` }}>
            
              <AppImage
              src={dest?.img}
              alt={dest?.alt}
              fill
              className="object-cover" />
            
              <div className="dest-card-overlay" />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <span className="text-lg mb-1">{dest?.flag}</span>
                <h3
                className="font-serif font-bold text-cream text-xl mb-1"
                style={{ fontFamily: 'Fraunces, serif' }}>
                
                  {t(`dest_${dest?.key}`)}
                </h3>
                <p className="text-cream/50 text-xs">{t(`dest_${dest?.key}_desc`)}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}